import React, { useEffect, useMemo, useRef, useState } from "react";
import { Col, Row, Collapse, ProgressBar } from "react-bootstrap";
import { getFullImageUrl } from "../../utils/getFullImageUrl";
import {
  FaMapMarkerAlt,
  FaBriefcase,
  FaCamera,
  FaFileAlt,
  FaCheckCircle,
  FaExclamationCircle,
  FaSave,
  FaImage,
  FaAlignLeft,
  FaHardHat,
  FaExclamationTriangle,
  FaTrash,
  FaMoneyBillWave,
  FaImages,
  FaUser,
  FaBuilding,
  FaPen,
  FaCheck,
  FaSearch,
  FaInfoCircle,
} from "react-icons/fa";

/* ═══════════════════════════ config ═══════════════════════════ */

const TAXONOMY_API_URLS = {
  local: "http://localhost:8000/api/trades/schema/",
  staging: "https://staging.kaakazini.com/api/trades/schema/",
  production: "https://kaakazini.com/api/trades/schema/",
};

const APP_ENV = process.env.REACT_APP_ENV || "local";
const TAXONOMY_URL = TAXONOMY_API_URLS[APP_ENV] || TAXONOMY_API_URLS.local;

const BRAND = { yellow: "#fff000", black: "#000000", white: "#ffffff" };

const MAX_PORTFOLIO = 8;
const MAX_IMAGE_MB = 10; 
const SERVICE_SEARCH_THRESHOLD = 10; 
const ACCOUNT_TYPES = [
  { value: "Individual", label: "Individual", icon: <FaUser size={11} /> },
  { value: "Company", label: "Company", icon: <FaBuilding size={11} /> },
];

const UNIT_LABELS = {
  fixed: "fixed price",
  hour: "per hour",
  day: "per day",
  sqm: "per m²",
};

const STEP_REF = {
  photo: "photo",
  company: "account",
  description: "description",
  profession: "profession",
  specialisation: "specialisation",
  services: "services",
  location: "location",
  portfolio: "portfolio",
};

const EMPTY_TAXONOMY = { trades: [], specialisations: {}, services: {} };

/* ═══════════════════════════ helpers ═══════════════════════════ */

const getFileName = (fileOrUrl) => {
  if (!fileOrUrl) return null;
  if (typeof fileOrUrl === "string" && fileOrUrl.startsWith("blob:")) return "New file selected";
  if (typeof fileOrUrl !== "string" || !fileOrUrl.includes("/")) return fileOrUrl;
  return decodeURIComponent(fileOrUrl.split("/").pop().split("?")[0]) || "document";
};

const newId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;

const toSkillList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string") {
    return value.split(",").map((item) => item.trim()).filter(Boolean);
  }
  return [];
};

const normalizeTaxonomy = (data) => ({
  trades: Array.isArray(data?.trades) ? data.trades : [],
  specialisations:
    data?.specialisations && typeof data.specialisations === "object" && !Array.isArray(data.specialisations)
      ? data.specialisations
      : {},
  services:
    data?.services && typeof data.services === "object" && !Array.isArray(data.services)
      ? data.services
      : {},
});

const isValidTaxonomy = (value) =>
  value &&
  Array.isArray(value.trades) &&
  value.trades.length > 0 &&
  value.specialisations &&
  typeof value.specialisations === "object" &&
  value.services &&
  typeof value.services === "object";

const formatRate = (service) => {
  if (service.rate === null || service.rate === undefined || service.rate === "") {
    return "Price agreed per job";
  }
  const amount = Number(service.rate);
  if (Number.isNaN(amount)) return "Price agreed per job";
  return `KSh ${amount.toLocaleString()} ${UNIT_LABELS[service.unit || "fixed"] || ""}`.trim();
};

const calcCompletion = (steps) =>
  Math.round((steps.filter((s) => s.done).length / steps.length) * 100);

const buildFormData = ({
  profileData,
  profileImage,
  proofDocument,
  portfolioImages = [],
  portfolioRemoveIds = [],
}) => {
  const fd = new FormData();

  
  [
    "description",
    "profession",
    "location",
    "company_name",
    "account_type",
    "experience_level",
  ].forEach((field) => {
    if (profileData[field] !== undefined && profileData[field] !== null) {
      fd.append(field, profileData[field]);
    }
  });

  const skills = JSON.stringify(toSkillList(profileData.specialisation));
  fd.append("specialisation", skills);
  fd.append("skills", skills);

  if (Array.isArray(profileData.services)) {
    const payload = profileData.services.map((service) => ({
      name: service.name,
      rate: service.rate ?? null,
      unit: service.unit ?? "fixed",
    }));
    fd.append("services", JSON.stringify(payload));
  }

  if (profileData.video instanceof File) fd.append("video", profileData.video);
  if (profileImage instanceof File) fd.append("profile", profileImage);
  if (proofDocument instanceof File) fd.append("proof_document", proofDocument);

  portfolioImages.forEach((image) => {
    if (image.file instanceof File) fd.append("portfolio_images", image.file);
  });

  if (portfolioRemoveIds.length > 0) {
    fd.append("portfolio_remove_ids", JSON.stringify(portfolioRemoveIds));
  }

  return fd;
};

// Object-URL preview for a File, or the full URL for a saved image
const useFilePreview = (file) => {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    if (!file) {
      setUrl(null);
      return undefined;
    }
    if (file instanceof File) {
      const objectUrl = URL.createObjectURL(file);
      setUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
    setUrl(getFullImageUrl(file));
    return undefined;
  }, [file]);

  return url;
};

/* ═══════════════════════════ small UI pieces ═══════════════════════════ */

const ValidationBanner = ({ message, onDismiss }) => {
  if (!message) return null;
  return (
    <div className="validation-banner" role="alert">
      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <FaExclamationTriangle />
        {message}
      </span>
      <button type="button" onClick={onDismiss} aria-label="Dismiss" className="validation-close">
        ×
      </button>
    </div>
  );
};

const StatusBanner = ({ status }) => {
  const map = {
    pending: {
      className: "status-pending",
      msg: "Your profile is being reviewed. We will tell you once it is approved.",
    },
    approved: {
      className: "status-approved",
      msg: "Your profile is live. Clients can find and hire you.",
    },
    rejected: {
      className: "status-rejected",
      msg: "Your profile was not approved. Update the details below and submit again.",
    },
  };
  const cfg = map[status];
  if (!cfg) return null;
  return <div className={`status-banner ${cfg.className}`}>{cfg.msg}</div>;
};

const ConfirmInline = ({ message, confirmLabel = "Yes, remove", cancelLabel = "Keep", onConfirm, onCancel }) => (
  <div className="confirm-inline" role="alertdialog" aria-label={message}>
    <span className="confirm-text">{message}</span>
    <span className="confirm-actions">
      <button type="button" className="confirm-yes" onClick={onConfirm}>
        {confirmLabel}
      </button>
      <button type="button" className="confirm-no" onClick={onCancel}>
        {cancelLabel}
      </button>
    </span>
  </div>
);

const Section = ({ number, children, completed = false, className = "" }) => (
  <section className={`pf-section ${className}`}>
    <div className={`profile-step-number ${completed ? "profile-step-complete" : ""}`}>
      {completed ? <FaCheckCircle size={16} /> : number}
    </div>
    <div className="pf-section-content">{children}</div>
  </section>
);

export const TextInputWithDatalist = ({
  label,
  name,
  value,
  onChange,
  options = [],
  required,
  icon,
  placeholder,
  helpText,
  as = "input",
  rows = 4,
  maxLength,
  showCount = false,
}) => {
  const [touched, setTouched] = useState(false);
  const hasError = touched && required && !String(value || "").trim();
  const length = String(value || "").length;

  return (
    <div className="pf-field mb-4">
      <label className="pf-label" htmlFor={`field-${name}`}>
        {icon && <span className="pf-icon">{icon}</span>}
        {label}
        {required && <span className="required-star"> *</span>}
      </label>

      {helpText && <small className="pf-help">{helpText}</small>}

      {as === "textarea" ? (
        <textarea
          id={`field-${name}`}
          className={`pf-input pf-textarea ${hasError ? "pf-err" : ""}`}
          name={name}
          value={value || ""}
          onChange={onChange}
          onBlur={() => setTouched(true)}
          placeholder={placeholder}
          rows={rows}
          maxLength={maxLength}
        />
      ) : (
        <input
          id={`field-${name}`}
          list={options.length ? `${name}-opts` : undefined}
          className={`pf-input ${hasError ? "pf-err" : ""}`}
          name={name}
          value={value || ""}
          onChange={onChange}
          onBlur={() => setTouched(true)}
          placeholder={placeholder}
          maxLength={maxLength}
          autoComplete="off"
        />
      )}

      {showCount && (
        <div className="char-count">
          {length}
          {maxLength ? ` / ${maxLength}` : ""} characters
        </div>
      )}

      {options.length > 0 && (
        <datalist id={`${name}-opts`}>
          {options.map((option) => (
            <option key={option} value={option} />
          ))}
        </datalist>
      )}

      {hasError && (
        <small className="pf-error-msg">
          <FaExclamationCircle className="me-1" />
          {label} is required
        </small>
      )}
    </div>
  );
};

const Chip = ({ label, selected, onClick, icon }) => (
  <button
    type="button"
    className={`chip-toggle ${selected ? "chip-toggle-on" : ""}`}
    onClick={onClick}
    aria-pressed={selected}
  >
    {icon && !selected && <span style={{ marginRight: 5 }}>{icon}</span>}
    {selected && <FaCheckCircle size={11} style={{ marginRight: 5 }} />}
    {label}
  </button>
);

const ProfessionSelect = ({ value, options = [], onChange, legacyValue, loading = false }) => (
  <div className="pf-field mb-4">
    <label className="pf-label" htmlFor="field-profession">
      <span className="pf-icon"><FaHardHat /></span>
      Your trade
      <span className="required-star"> *</span>
    </label>

    <small className="pf-help">
      Pick the trade that best describes your work. If you change it later, we will ask before
      clearing your specialities and services.
    </small>

    <select
      id="field-profession"
      className="pf-input"
      name="profession"
      value={value}
      onChange={onChange}
      disabled={loading}
    >
      <option value="">{loading ? "Loading trades..." : "Select your trade"}</option>
      {options.map((trade) => (
        <option key={trade} value={trade}>{trade}</option>
      ))}
    </select>

    {legacyValue && !loading && (
      <div className="service-note">
        Your saved trade "{legacyValue}" is not on our approved list. Please choose the closest
        trade above, then pick your specialities and services.
      </div>
    )}

    {!loading && options.length === 0 && (
      <div className="service-note">
        The trade list could not be loaded. Please refresh the page and try again.
      </div>
    )}
  </div>
);

const SkillsPicker = ({ value = [], options = [], onChange, disabled, loading = false }) => {
  const toggle = (option) =>
    onChange(value.includes(option) ? value.filter((item) => item !== option) : [...value, option]);

  return (
    <div className="pf-field mb-4">
      <label className="pf-label">
        <span className="pf-icon"><FaHardHat /></span>
        Your specialities
        <span className="required-star"> *</span>
        {value.length > 0 && (
          <button type="button" className="link-btn ms-auto" onClick={() => onChange([])}>
            Clear all
          </button>
        )}
      </label>

      <small className="pf-help">Tap everything you are good at. You can pick more than one.</small>

      {loading ? (
        <div className="service-note">Loading specialities...</div>
      ) : disabled ? (
        <div className="service-note">Choose your trade first (step 3).</div>
      ) : options.length === 0 ? (
        <div className="service-note">No specialities are available for this trade yet.</div>
      ) : (
        <div className="chip-row">
          {options.map((option) => (
            <Chip key={option} label={option} selected={value.includes(option)} onClick={() => toggle(option)} />
          ))}
        </div>
      )}
    </div>
  );
};

export const FileInput = ({ label, file, onChange, accept = "image/*", required, hint }) => {
  const [touched, setTouched] = useState(false);
  const previewUrl = useFilePreview(file);
  const ref = useRef();

  const hasError = touched && required && !file;

  return (
    <div className="pf-field mb-4">
      <label className="pf-label">
        {label}
        {required && <span className="required-star"> *</span>}
      </label>

      {hint && <small className="pf-help">{hint}</small>}

      <div
        className={`file-zone ${hasError ? "fz-error" : ""} ${file ? "fz-filled" : ""}`}
        onClick={() => ref.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && ref.current?.click()}
        aria-label={file ? `Change ${label}` : `Upload ${label}`}
      >
        {previewUrl && accept.includes("image") ? (
          <div style={{ position: "relative", width: "100%" }}>
            <img
              src={previewUrl}
              alt={label}
              style={{ width: "100%", maxHeight: 240, objectFit: "cover", display: "block" }}
            />
            <div className="fz-overlay">
              <FaCamera size={18} />
              <span>Change photo</span>
            </div>
          </div>
        ) : (
          <div className="fz-placeholder">
            <FaImage size={30} style={{ opacity: 0.3, marginBottom: 8 }} />
            <span className="fz-text">Tap to upload</span>
          </div>
        )}
      </div>

      <input
        ref={ref}
        type="file"
        style={{ display: "none" }}
        accept={accept}
        onChange={(event) => {
          setTouched(true);
          onChange(event);
        }}
      />

      {file && (
        <div className="fname-tag">
          <FaCheckCircle />
          {getFileName(file instanceof File ? file.name : file)}
        </div>
      )}

      {hasError && (
        <small className="pf-error-msg">
          <FaExclamationCircle className="me-1" />
          Required
        </small>
      )}
    </div>
  );
};

const ProofDocumentInput = ({ proofDocument, onChange, onRemove }) => {
  const ref = useRef();
  const [confirming, setConfirming] = useState(false);

  const displayName = proofDocument
    ? proofDocument instanceof File
      ? proofDocument.name
      : getFileName(proofDocument)
    : null;

  return (
    <div className="pf-field mb-4">
      <label className="pf-label">
        <span className="pf-icon"><FaFileAlt /></span>
        Proof document
        <span className="optional-text">(optional)</span>
      </label>

      <small className="pf-help">
        A certificate, ID or trade licence helps us verify you faster.
      </small>

      <div
        className={`file-zone ${proofDocument ? "fz-filled" : ""}`}
        onClick={() => ref.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && ref.current?.click()}
        aria-label={proofDocument ? "Replace proof document" : "Upload proof document"}
      >
        <div className="fz-placeholder">
          <FaFileAlt size={24} style={{ opacity: 0.3, marginBottom: 6 }} />
          <span className="fz-text">{displayName || "Tap to upload a document"}</span>
        </div>
      </div>

      <input
        ref={ref}
        type="file"
        style={{ display: "none" }}
        accept=".pdf,image/*"
        onChange={onChange}
      />

      {proofDocument && (
        <div className="fname-row">
          <div className="fname-tag">
            <FaCheckCircle />
            {displayName}
          </div>

          {onRemove && !confirming && (
            <button type="button" className="link-btn danger" onClick={() => setConfirming(true)}>
              <FaTrash size={10} /> Remove
            </button>
          )}
        </div>
      )}

      {confirming && (
        <ConfirmInline
          message="Remove this document?"
          onConfirm={() => {
            setConfirming(false);
            onRemove?.();
          }}
          onCancel={() => setConfirming(false)}
        />
      )}
    </div>
  );
};

/* ═══════════════════════════ services editor ═══════════════════════════ */

const ServicesEditor = ({
  services = [],
  serviceOptions = [],
  onToggle,
  onRemove,
  onUpdateRate,
  required,
  disabled,
  touched,
  setTouched,
  loading = false,
}) => {
  const [query, setQuery] = useState("");
  const [confirmName, setConfirmName] = useState(null);

  const hasError = touched && required && services.length === 0;
  const selectedNames = services.map((service) => service.name);

  const showSearch = serviceOptions.length > SERVICE_SEARCH_THRESHOLD;
  const visibleOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? serviceOptions.filter((name) => name.toLowerCase().includes(q)) : serviceOptions;
  }, [serviceOptions, query]);

  const handleChip = (name) => {
    setTouched(true);
    const existing = services.find((service) => service.name === name);
    if (existing && existing.rate !== null && existing.rate !== undefined && existing.rate !== "") {
      setConfirmName(name);
      return;
    }
    onToggle(name);
  };

  return (
    <div className="pf-field mb-4">
      <label className="pf-label">
        <span className="pf-icon"><FaBriefcase /></span>
        Services you offer
        {required && <span className="required-star"> *</span>}
      </label>

      <small className="pf-help">
        Tap the services you provide. The first one you pick becomes your main service.
      </small>

      {loading ? (
        <div className="service-note">Loading services...</div>
      ) : disabled ? (
        <div className="service-note">Choose your trade first (step 3).</div>
      ) : serviceOptions.length === 0 ? (
        <div className="service-note">No services are available for this trade yet.</div>
      ) : (
        <>
          {showSearch && (
            <div className="search-box">
              <FaSearch size={12} />
              <input
                type="search"
                className="search-input"
                placeholder="Search services..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search services"
              />
            </div>
          )}

          <div className="chip-row">
            {visibleOptions.map((name) => (
              <Chip key={name} label={name} selected={selectedNames.includes(name)} onClick={() => handleChip(name)} />
            ))}
            {visibleOptions.length === 0 && <div className="service-note">No service matches "{query}".</div>}
          </div>
        </>
      )}

      {services.length > 0 && (
        <div className="services-list mt-4">
          <div className="services-list-title">
            Your services ({services.length}) — add a price if you can
          </div>

          {services.map((service, index) => {
            const confirming = confirmName === service.name;
            return (
              <div key={service.id ?? `${service.name}-${index}`} className="service-row">
                <div className="service-main">
                  {index === 0 && <span className="primary-badge">MAIN</span>}
                  <span className="service-name">{service.name}</span>
                </div>

                {confirming ? (
                  <ConfirmInline
                    message={`Remove ${service.name} and its price?`}
                    onConfirm={() => {
                      setConfirmName(null);
                      onRemove(service.name);
                    }}
                    onCancel={() => setConfirmName(null)}
                  />
                ) : (
                  <div className="service-controls">
                    <div className="rate-wrapper">
                      <span className="currency-tag">KSh</span>
                      <input
                        type="number"
                        inputMode="decimal"
                        className="pf-input rate-input"
                        placeholder="Price"
                        min="0"
                        aria-label={`Price for ${service.name}`}
                        value={service.rate ?? ""}
                        onChange={(event) => onUpdateRate(service.name, event.target.value, service.unit)}
                      />
                    </div>

                    <select
                      className="pf-input unit-select"
                      aria-label={`Pricing unit for ${service.name}`}
                      value={service.unit || "fixed"}
                      onChange={(event) => onUpdateRate(service.name, service.rate ?? "", event.target.value)}
                    >
                      <option value="fixed">Fixed price</option>
                      <option value="hour">Per hour</option>
                      <option value="day">Per day</option>
                      <option value="sqm">Per m²</option>
                    </select>

                    <button
                      className="svc-remove-btn"
                      type="button"
                      onClick={() => setConfirmName(service.name)}
                      title="Remove service"
                      aria-label={`Remove ${service.name}`}
                    >
                      <FaTrash size={11} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="service-note">
        <FaInfoCircle style={{ marginRight: 6 }} />
        Prices are optional. If the price depends on the job, leave it blank and agree it with the
        client.
      </div>

      {hasError && (
        <small className="pf-error-msg mt-2">
          <FaExclamationCircle className="me-1" />
          Pick at least one service
        </small>
      )}
    </div>
  );
};

/* ═══════════════════════════ portfolio gallery ═══════════════════════════ */

const PortfolioGallery = ({ images = [], onAdd, onRemove, required }) => {
  const ref = useRef();
  const [touched, setTouched] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [notice, setNotice] = useState("");

  const hasError = touched && required && images.length === 0;

  const handleFiles = (event) => {
    setTouched(true);

    const files = Array.from(event.target.files || []);
    const room = MAX_PORTFOLIO - images.length;

    let tooBig = 0;
    const accepted = [];
    files.forEach((file) => {
      if (!file.type.startsWith("image/")) return;
      if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
        tooBig += 1;
        return;
      }
      accepted.push(file);
    });

    accepted.slice(0, Math.max(room, 0)).forEach((file) => {
      onAdd({ id: newId(), file, preview: URL.createObjectURL(file) });
    });

    const messages = [];
    if (tooBig) {
      messages.push(`${tooBig} photo${tooBig > 1 ? "s were" : " was"} skipped because each photo must be under ${MAX_IMAGE_MB} MB.`);
    }
    if (accepted.length > room) {
      messages.push(`You can add up to ${MAX_PORTFOLIO} photos, so some were skipped.`);
    }
    setNotice(messages.join(" "));

    event.target.value = "";
  };

  const getImgSrc = (image) => image.preview || getFullImageUrl(image.url) || "";

  const removeImage = (image, index) => {
    const id = image.id ?? index;
    const previewUrl = image.preview;
    setConfirmId(null);
    onRemove(id);
    if (previewUrl && previewUrl.startsWith("blob:")) {
      setTimeout(() => URL.revokeObjectURL(previewUrl), 0);
    }
  };

  return (
    <div className="pf-field mb-4">
      <label className="pf-label">
        <span className="pf-icon"><FaImages /></span>
        Photos of your work
        {required && <span className="required-star"> *</span>}
        <span className="photo-count">{images.length} / {MAX_PORTFOLIO}</span>
      </label>

      <small className="pf-help">
        Show clients jobs you have finished. Your first photo is your cover photo.
      </small>

      <div className="portfolio-grid">
        {images.map((image, index) => {
          const id = image.id ?? index;
          const isNew = image.file instanceof File;
          const confirming = confirmId === id;

          return (
            <div key={id} className="portfolio-thumb">
              <img src={getImgSrc(image)} alt={`Work ${index + 1}`} className="portfolio-img" />

              {index === 0 && <div className="portfolio-cover-badge">Cover</div>}
              {isNew && <div className="portfolio-new-badge">New</div>}

              {confirming ? (
                <div className="portfolio-confirm">
                  <span>Remove this photo?</span>
                  <div>
                    <button type="button" className="confirm-yes" onClick={() => removeImage(image, index)}>
                      Remove
                    </button>
                    <button type="button" className="confirm-no" onClick={() => setConfirmId(null)}>
                      Keep
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  className="portfolio-remove"
                  type="button"
                  onClick={() => setConfirmId(id)}
                  title="Remove photo"
                  aria-label={`Remove photo ${index + 1}`}
                >
                  <FaTrash size={11} />
                </button>
              )}
            </div>
          );
        })}

        {images.length < MAX_PORTFOLIO && (
          <div
            className={`portfolio-add-slot ${hasError ? "fz-error" : ""}`}
            onClick={() => {
              setTouched(true);
              ref.current?.click();
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && ref.current?.click()}
            aria-label="Add work photos"
          >
            <FaCamera size={22} style={{ opacity: 0.3, marginBottom: 5 }} />
            <span className="add-photo-text">{images.length === 0 ? "Add photos" : "Add more"}</span>
          </div>
        )}
      </div>

      <input ref={ref} type="file" style={{ display: "none" }} accept="image/*" multiple onChange={handleFiles} />

      {notice && (
        <div className="service-note">
          <FaInfoCircle style={{ marginRight: 6 }} />
          {notice}
        </div>
      )}

      {hasError && (
        <small className="pf-error-msg mt-1">
          <FaExclamationCircle className="me-1" />
          Add at least one photo of your work
        </small>
      )}
    </div>
  );
};

/* ═══════════════════════════ account type ═══════════════════════════ */

const AccountTypeInfo = ({ accountType }) => {
  const info = {
    Individual: ["You work on your own.", "Clients can book you directly."],
    Company: ["Your business name shows on your profile and quotes.", "You can manage helpers and foremen."],
  }[accountType];

  if (!info) return null;

  return (
    <div className="account-info">
      <strong>{accountType === "Company" ? "Business account" : "Individual account"}</strong>
      <ul>
        {info.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  );
};

const AccountTypeSelector = ({ value, onChange }) => (
  <div className="pf-field mb-4">
    <label className="pf-label">
      <span className="pf-icon"><FaBriefcase /></span>
      Account type
      <span className="required-star"> *</span>
    </label>

    <small className="pf-help">
      Choose Individual if you work alone, or Company if you run a business with a team.
    </small>

    <div className="chip-row">
      {ACCOUNT_TYPES.map((type) => (
        <Chip
          key={type.value}
          label={type.label}
          icon={type.icon}
          selected={value === type.value}
          onClick={() => onChange(type.value)}
        />
      ))}
    </div>

    <AccountTypeInfo accountType={value} />
  </div>
);

/* ═══════════════════════════ progress + summary ═══════════════════════════ */

const ProgressCard = ({ percent, steps, onStepClick }) => (
  <div className="profile-progress">
    <div className="profile-progress-top">
      <span className="profile-progress-title">
        {percent === 100 ? "Your profile is complete" : "Complete your profile"}
      </span>
      <span className="profile-progress-value">{percent}%</span>
    </div>

    <ProgressBar now={percent} aria-label="Profile completion" />

    <div className="profile-step-list">
      {steps.map((step) => (
        <button
          type="button"
          key={step.key}
          className={`profile-step-pill ${step.done ? "done" : ""}`}
          onClick={() => onStepClick(step.key)}
          title={step.done ? "Done — tap to edit" : "Tap to fill this in"}
        >
          {step.done ? <FaCheck size={9} /> : <span className="pill-dot" />}
          {step.label}
        </button>
      ))}
    </div>
  </div>
);

const SummaryBlock = ({ icon, title, onEdit, isEmpty, emptyText, children }) => (
  <div className="sum-block">
    <div className="sum-head">
      <h4 className="sum-title">
        {icon}
        <span>{title}</span>
      </h4>
      <button type="button" className="sum-edit" onClick={onEdit}>
        <FaPen size={10} /> {isEmpty ? "Add" : "Edit"}
      </button>
    </div>
    {isEmpty ? <p className="sum-empty">{emptyText}</p> : children}
  </div>
);

const ProfileSummary = ({
  craftsman,
  profileData,
  profileImage,
  accountType,
  isCompany,
  trade,
  skills,
  services,
  portfolioImages,
  proofDocument,
  onEdit,
  onDeleteProfile,
  deleting,
}) => {
  const photo = useFilePreview(profileImage);
  const name = craftsman?.full_name || craftsman?.name || "Your profile";
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [typed, setTyped] = useState("");

  return (
    <div className="summary">
      <div className="sum-hero">
        {photo ? (
          <img src={photo} alt={name} className="sum-avatar" />
        ) : (
          <div className="sum-avatar sum-avatar-empty">{name.charAt(0).toUpperCase()}</div>
        )}

        <div className="sum-hero-text">
          <h3 className="sum-name">{name}</h3>
          <div className="sum-sub">
            {trade || "No trade selected"}
            {profileData.location ? ` · ${profileData.location}` : ""}
          </div>
          <span className="sum-type">{accountType}</span>
        </div>

        <button type="button" className="save-btn sum-edit-all" onClick={() => onEdit(null)}>
          <FaPen size={12} /> Edit profile
        </button>
      </div>

      <SummaryBlock
        icon={<FaCamera />}
        title="Photo & account"
        onEdit={() => onEdit(isCompany && !profileData.company_name?.trim() ? "company" : "photo")}
        isEmpty={!profileImage}
        emptyText="Add a clear photo of yourself."
      >
        <p className="sum-text">
          {isCompany ? `Company: ${profileData.company_name || "name not added yet"}` : "Individual account"}
        </p>
        {proofDocument && (
          <p className="sum-text">
            <FaFileAlt size={12} style={{ marginRight: 6 }} />
            Proof document: {proofDocument instanceof File ? proofDocument.name : getFileName(proofDocument)}
          </p>
        )}
      </SummaryBlock>

      <SummaryBlock
        icon={<FaAlignLeft />}
        title="About you"
        onEdit={() => onEdit("description")}
        isEmpty={!profileData.description?.trim()}
        emptyText="Tell clients about your experience."
      >
        <p className="sum-text sum-pre">{profileData.description}</p>
      </SummaryBlock>

      <SummaryBlock
        icon={<FaHardHat />}
        title="Trade & specialities"
        onEdit={() => onEdit(trade ? "specialisation" : "profession")}
        isEmpty={!trade}
        emptyText="Choose your trade and what you are good at."
      >
        <p className="sum-text"><strong>{trade}</strong></p>
        {skills.length > 0 ? (
          <div className="chip-row">
            {skills.map((skill) => (
              <span key={skill} className="sum-chip">{skill}</span>
            ))}
          </div>
        ) : (
          <p className="sum-empty">No specialities picked yet.</p>
        )}
      </SummaryBlock>

      <SummaryBlock
        icon={<FaMoneyBillWave />}
        title="Services & prices"
        onEdit={() => onEdit("services")}
        isEmpty={services.length === 0}
        emptyText="Pick the services you offer."
      >
        <ul className="sum-list">
          {services.map((service, index) => (
            <li key={service.id ?? service.name}>
              <span>
                {index === 0 && <span className="primary-badge">MAIN</span>} {service.name}
              </span>
              <span className="sum-price">{formatRate(service)}</span>
            </li>
          ))}
        </ul>
      </SummaryBlock>

      <SummaryBlock
        icon={<FaMapMarkerAlt />}
        title="Where you work"
        onEdit={() => onEdit("location")}
        isEmpty={!profileData.location?.trim()}
        emptyText="Add your area or town."
      >
        <p className="sum-text">{profileData.location}</p>
      </SummaryBlock>

      <SummaryBlock
        icon={<FaImages />}
        title={`Photos of your work (${portfolioImages.length})`}
        onEdit={() => onEdit("portfolio")}
        isEmpty={portfolioImages.length === 0}
        emptyText="Add photos of jobs you have finished."
      >
        <div className="sum-thumbs">
          {portfolioImages.map((image, index) => (
            <img
              key={image.id ?? index}
              src={image.preview || getFullImageUrl(image.url) || ""}
              alt={`Work ${index + 1}`}
              className="sum-thumb"
            />
          ))}
        </div>
      </SummaryBlock>

      {onDeleteProfile && (
        <div className="danger-zone">
          <div className="danger-title">Delete profile</div>
          <p className="danger-text">
            This permanently removes your profile, services and photos. This cannot be undone.
          </p>

          {!confirmDelete ? (
            <button type="button" className="danger-btn" onClick={() => setConfirmDelete(true)}>
              <FaTrash size={11} /> Delete my profile
            </button>
          ) : (
            <div className="danger-confirm">
              <label htmlFor="delete-confirm" className="danger-text">
                Type <strong>DELETE</strong> to confirm:
              </label>
              <input
                id="delete-confirm"
                className="pf-input"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoComplete="off"
              />
              <div className="danger-actions">
                <button
                  type="button"
                  className="danger-btn solid"
                  disabled={typed !== "DELETE" || deleting}
                  onClick={onDeleteProfile}
                >
                  {deleting ? "Deleting..." : "Yes, delete everything"}
                </button>
                <button
                  type="button"
                  className="confirm-no"
                  onClick={() => {
                    setConfirmDelete(false);
                    setTyped("");
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


function ProfileTab({
  craftsman,
  profileData,
  setProfileData,

  profileImage,
  handleProfileImageChange,

  handleProofDocumentChange,
  proofDocument,
  onProofDocumentRemove,

  taxonomy,

  portfolioImages = [],
  onPortfolioAdd,
  onPortfolioRemove,
  portfolioRemoveIds = [],

  saveProfile,
  saving = false,
  addToast,

  selectedServices,
  onToggleService,
  onRemoveService,
  onUpdateServiceRate,

  onDeleteProfile,
  deleting = false,

  focusSection,
  onFocusHandled,
}) {
  const [validationError, setValidationError] = useState("");
  const [servicesTouched, setServicesTouched] = useState(false);
  const [loadedTaxonomy, setLoadedTaxonomy] = useState(EMPTY_TAXONOMY);
  const [taxonomyLoading, setTaxonomyLoading] = useState(true);
  const [taxonomyError, setTaxonomyError] = useState("");

  const [mode, setMode] = useState(null); 
  const [pendingFocus, setPendingFocus] = useState(null);
  const [pendingTrade, setPendingTrade] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const snapshotRef = useRef(null);
  const topRef = useRef(null);

  const locations = [
    "Nairobi CBD", "Westlands", "Karen", "South B", "Embakasi", "Kasarani", "Kileleshwa",
    "Lavington", "Parklands", "Ruaka", "Nakuru", "Eldoret", "Kisumu", "Mombasa", "Thika", "Machakos",
  ];

  const sectionRefs = {
    photo: useRef(null),
    account: useRef(null),
    description: useRef(null),
    profession: useRef(null),
    specialisation: useRef(null),
    services: useRef(null),
    location: useRef(null),
    portfolio: useRef(null),
  };

  const accountType = profileData.account_type || "Individual";
  const isCompany = accountType === "Company";

  const hasSavedProfile = !!(
    craftsman &&
    (craftsman.profession || craftsman.description || craftsman.primary_service || craftsman.status)
  );

  const currentMode = mode ?? (hasSavedProfile ? "view" : "edit");

  /* ───────── taxonomy ───────── */

  useEffect(() => {
    const controller = new AbortController();

    const loadTaxonomy = async () => {
      try {
        setTaxonomyLoading(true);
        setTaxonomyError("");

        const response = await fetch(TAXONOMY_URL, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          signal: controller.signal,
        });

        if (!response.ok) throw new Error(`Taxonomy request failed with status ${response.status}`);

        const normalized = normalizeTaxonomy(await response.json());
        if (normalized.trades.length === 0) throw new Error("The trade schema returned no trades.");

        setLoadedTaxonomy(normalized);
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error("Failed to load KaaKazini trade taxonomy:", error);
        setLoadedTaxonomy(EMPTY_TAXONOMY);
        setTaxonomyError(
          "We could not load the approved trade list. Please refresh the page and try again."
        );
      } finally {
        if (!controller.signal.aborted) setTaxonomyLoading(false);
      }
    };

    loadTaxonomy();
    return () => controller.abort();
  }, []);

  const activeTaxonomy = useMemo(
    () => (isValidTaxonomy(taxonomy) ? normalizeTaxonomy(taxonomy) : loadedTaxonomy),
    [taxonomy, loadedTaxonomy]
  );

  const tradeOptions = useMemo(
    () => (Array.isArray(activeTaxonomy.trades) ? activeTaxonomy.trades : []),
    [activeTaxonomy]
  );

  const savedProfession = (profileData.profession || "").trim();

  // Match the saved profession against the backend list, ignoring capital letters
  const trade =
    tradeOptions.find((option) => String(option).toLowerCase() === savedProfession.toLowerCase()) || "";

  const legacyProfession = !taxonomyLoading && savedProfession && !trade ? savedProfession : "";

  const skillOptions = useMemo(
    () => (trade ? activeTaxonomy.specialisations?.[trade] ?? [] : []),
    [trade, activeTaxonomy]
  );

  const serviceOptions = useMemo(
    () => (trade ? activeTaxonomy.services?.[trade] ?? [] : []),
    [trade, activeTaxonomy]
  );
  const skills = useMemo(
    () => toSkillList(profileData.specialisation).filter((skill) => skillOptions.includes(skill)),
    [profileData.specialisation, skillOptions]
  );

  const services = useMemo(
    () =>
      (selectedServices ?? profileData.services ?? []).filter((service) =>
        serviceOptions.includes(service.name)
      ),
    [selectedServices, profileData.services, serviceOptions]
  );

  /* ───────── progress ───────── */

  const steps = [
    { key: "photo", label: "Photo", done: !!profileImage },
    ...(isCompany
      ? [{ key: "company", label: "Company name", done: !!profileData.company_name?.trim() }]
      : []),
    { key: "description", label: "About you", done: !!profileData.description?.trim() },
    { key: "profession", label: "Trade", done: !!trade },
    { key: "specialisation", label: "Specialities", done: skills.length > 0 },
    { key: "services", label: "Services", done: services.length > 0 },
    { key: "location", label: "Area", done: !!profileData.location?.trim() },
    { key: "portfolio", label: "Work photos", done: portfolioImages.length > 0 },
  ];

  const missing = steps.filter((step) => !step.done);
  const completion = calcCompletion(steps);

  /* ───────── navigation helpers ───────── */

  const startEdit = (sectionKey = null) => {
    snapshotRef.current = profileData;
    setDirty(false);
    setConfirmDiscard(false);
    setMode("edit");
    if (sectionKey) setPendingFocus(sectionKey);
    else topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scrollToSection = (key) => {
    const refKey = STEP_REF[key] || key;
    sectionRefs[refKey]?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // A parent can ask us to jump to a section (e.g. "Complete your profile" links)
  useEffect(() => {
    if (focusSection && currentMode === "view") startEdit(focusSection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusSection]);

  const focusTarget = pendingFocus || focusSection;

  useEffect(() => {
    if (!focusTarget || currentMode !== "edit") return undefined;

    const timer = setTimeout(() => {
      scrollToSection(focusTarget);
      setPendingFocus(null);
      onFocusHandled?.();
    }, 120);

    return () => clearTimeout(timer);
  }, [focusTarget, currentMode]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const leaveEditor = () => {
    setConfirmDiscard(false);
    setPendingTrade(null);
    setDirty(false);
    setMode("view");
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const requestCancel = () => {
    if (dirty) setConfirmDiscard(true);
    else leaveEditor();
  };

  const discardChanges = () => {
    if (snapshotRef.current) setProfileData(snapshotRef.current);
    leaveEditor();
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setDirty(true);
    setProfileData((previous) => ({ ...previous, [name]: value }));
  };
  const handleAccountTypeChange = (type) => {
    setDirty(true);
    setProfileData((previous) => ({
      ...previous,
      account_type: type,
      company_name: previous.company_name || "",
    }));
    setValidationError("");
  };

  const applyProfession = (next) => {
    const allowedSkills = activeTaxonomy.specialisations?.[next] ?? [];
    const allowedServices = activeTaxonomy.services?.[next] ?? [];

    setDirty(true);
    setPendingTrade(null);
    setProfileData((previous) => ({
      ...previous,
      profession: next,
      specialisation: toSkillList(previous.specialisation).filter((skill) => allowedSkills.includes(skill)),
      services: (previous.services ?? []).filter((service) => allowedServices.includes(service.name)),
    }));
    setValidationError("");
    setServicesTouched(false);
  };

  const handleProfessionChange = (event) => {
    const next = event.target.value;
    if (next === trade) return;

    const lostSkills = skills.filter((s) => !(activeTaxonomy.specialisations?.[next] ?? []).includes(s)).length;
    const lostServices = services.filter((s) => !(activeTaxonomy.services?.[next] ?? []).includes(s.name)).length;
    if (trade && (lostSkills > 0 || lostServices > 0)) {
      setPendingTrade({ next, lostSkills, lostServices });
      return;
    }
    applyProfession(next);
  };

  const handleSkillsChange = (values) => {
    setDirty(true);
    setProfileData((previous) => ({ ...previous, specialisation: values }));
    setValidationError("");
  };

  const toggleService = (serviceName) => {
    if (!serviceOptions.includes(serviceName)) return;
    setDirty(true);

    if (onToggleService) {
      onToggleService(serviceName);
      return;
    }

    setProfileData((previous) => {
      const current = previous.services ?? [];
      const exists = current.some((service) => service.name === serviceName);
      return {
        ...previous,
        services: exists
          ? current.filter((service) => service.name !== serviceName)
          : [...current, { id: newId(), name: serviceName, rate: null, unit: "fixed" }],
      };
    });
    setValidationError("");
  };

  const removeService = (serviceName) => {
    setDirty(true);
    if (onRemoveService) {
      onRemoveService(serviceName);
      return;
    }
    setProfileData((previous) => ({
      ...previous,
      services: (previous.services ?? []).filter((service) => service.name !== serviceName),
    }));
  };

  const updateServiceRate = (serviceName, rate, unit) => {
    setDirty(true);
    if (onUpdateServiceRate) {
      onUpdateServiceRate(serviceName, rate, unit);
      return;
    }
    setProfileData((previous) => ({
      ...previous,
      services: (previous.services ?? []).map((service) => {
        if (service.name !== serviceName) return service;
        const parsed = rate === "" ? null : parseFloat(rate);
        return {
          ...service,
          rate: Number.isNaN(parsed) ? null : Math.max(parsed, 0),
          unit: unit || service.unit,
        };
      }),
    }));
  };
  const onProfileImage = (event) => {
    setDirty(true);
    handleProfileImageChange?.(event);
  };
  const onProofDoc = (event) => {
    setDirty(true);
    handleProofDocumentChange?.(event);
  };
  const onProofRemove = onProofDocumentRemove
    ? () => {
        setDirty(true);
        onProofDocumentRemove();
      }
    : undefined;
  const onPhotoAdd = (image) => {
    setDirty(true);
    onPortfolioAdd?.(image);
  };
  const onPhotoRemove = (id) => {
    setDirty(true);
    onPortfolioRemove?.(id);
  };

  /* ───────── validation + submit ───────── */

  const fail = (message, title, sectionKey) => {
    setValidationError(message);
    addToast?.(message, "warning", title);
    scrollToSection(sectionKey);
  };

  const handleSubmit = () => {
    setValidationError("");

    if (taxonomyLoading) {
      return fail("Please wait a moment while the trade list loads.", "Trade list", "profession");
    }

    if (taxonomyError || tradeOptions.length === 0) {
      return fail(
        "The trade list could not be loaded. Please refresh the page and try again.",
        "Trade list",
        "profession"
      );
    }

    if (missing.length > 0) {
      if (missing.some((step) => step.key === "services")) setServicesTouched(true);
      const names = missing.map((step) => step.label).join(", ");
      return fail(`Almost there! Still to fill in: ${names}.`, "Almost there", missing[0].key);
    }

    const result = saveProfile(
      buildFormData({
        profileData: {
          ...profileData,
          account_type: accountType,
          company_name: isCompany ? profileData.company_name.trim() : "",
          profession: trade,
          specialisation: skills,
          services,
        },
        profileImage,
        proofDocument,
        portfolioImages,
        portfolioRemoveIds,
      })
    );

    return Promise.resolve(result)
      .then((res) => {
        if (res === false) return;
        setDirty(false);
        setConfirmDiscard(false);
        setMode("view");
        topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      })
      .catch((error) => console.error("Saving profile failed:", error));
  };

  const saveLabel =
    craftsman?.status === "rejected"
      ? "Resubmit for approval"
      : hasSavedProfile
      ? "Save changes"
      : "Submit for approval";

  /* ═══════════════════════════ render ═══════════════════════════ */

  return (
    <>
      <style>{`
        .profile-flow { font-family:'Raleway',sans-serif; color:#000; }

        .validation-banner { background:#000; color:#fff; border-radius:12px; padding:.9rem 1.1rem; margin-bottom:1.25rem; display:flex; align-items:center; justify-content:space-between; gap:10px; font-weight:600; font-size:.875rem; }
        .validation-close { background:none; border:none; cursor:pointer; color:#fff; font-size:1.3rem; padding:0 4px; line-height:1; }

        .status-banner { border-radius:12px; padding:1rem 1.25rem; margin-bottom:1.25rem; font-weight:600; font-size:.875rem; }
        .status-pending { background:rgba(0,0,0,.06); color:#000; }
        .status-approved { background:#fff000; color:#000; }
        .status-rejected { background:#000; color:#fff; }

        /* progress */
        .profile-progress { background:#fff; border:2px solid rgba(0,0,0,.1); border-radius:14px; padding:1rem 1.15rem; margin-bottom:1.5rem; }
        .profile-progress-top { display:flex; justify-content:space-between; align-items:center; gap:1rem; margin-bottom:.65rem; }
        .profile-progress-title { font-weight:800; font-size:.9rem; }
        .profile-progress-value { font-weight:900; font-size:.9rem; }
        .profile-progress .progress { height:9px; background:rgba(0,0,0,.08); border-radius:20px; overflow:hidden; }
        .profile-progress .progress-bar { background:#fff000; border-radius:20px; }
        .profile-step-list { display:flex; flex-wrap:wrap; gap:.45rem; margin-top:.75rem; }
        .profile-step-pill { display:inline-flex; align-items:center; gap:.4rem; padding:.45rem .8rem; border-radius:999px; background:#f5f5f5; border:1px solid rgba(0,0,0,.12); font-size:.74rem; font-weight:700; cursor:pointer; font-family:inherit; color:#000; min-height:34px; }
        .profile-step-pill:hover { border-color:#000; }
        .profile-step-pill.done { background:#fff000; border-color:#000; }
        .pill-dot { width:9px; height:9px; border-radius:50%; border:2px solid rgba(0,0,0,.35); display:inline-block; }

        /* sections */
        .pf-section { background:#fff; border-radius:16px; border:2px solid rgba(0,0,0,.08); padding:1.5rem; margin-bottom:1.25rem; position:relative; overflow:hidden; }
        .pf-section::before { content:''; position:absolute; top:0; left:0; right:0; height:4px; background:#fff000; }
        .profile-step-number { position:absolute; top:1.5rem; left:1.5rem; width:36px; height:36px; min-width:36px; border-radius:50%; background:#000; color:#fff; display:flex; align-items:center; justify-content:center; font-weight:900; font-size:.85rem; }
        .profile-step-complete { background:#fff000; color:#000; }
        .pf-section-content { padding-left:2.7rem; }

        .pf-field { width:100%; }
        .pf-label { display:flex; align-items:center; gap:6px; font-weight:800; font-size:.9rem; color:#000; margin-bottom:.4rem; }
        .pf-icon { color:#000; font-size:.9rem; }
        .required-star { color:#000; font-weight:900; }
        .optional-text { color:rgba(0,0,0,.45); font-weight:400; font-size:.76rem; margin-left:4px; }
        .pf-help { display:block; color:rgba(0,0,0,.6); margin-bottom:.65rem; line-height:1.5; font-size:.8rem; }
        .char-count { text-align:right; font-size:.72rem; color:rgba(0,0,0,.45); margin-top:4px; }

        .pf-input { width:100%; padding:.8rem 1rem; border:2px solid rgba(0,0,0,.15); border-radius:10px; font-size:.95rem; background:#fff; color:#000; transition:border-color .2s, box-shadow .2s; display:block; font-family:inherit; }
        .pf-input:focus { outline:none; border-color:#000; box-shadow:0 0 0 4px rgba(255,240,0,.28); }
        .pf-input:disabled { background:#f5f5f5; color:rgba(0,0,0,.45); cursor:not-allowed; }
        .pf-textarea { resize:vertical; min-height:120px; }
        .pf-err { border-color:#000 !important; background:rgba(255,240,0,.08); }
        .pf-error-msg { color:#000; font-weight:700; font-size:.78rem; margin-top:4px; display:flex; align-items:center; }

        .link-btn { background:none; border:none; color:#000; text-decoration:underline; font-size:.76rem; font-weight:700; cursor:pointer; padding:2px 4px; font-family:inherit; }
        .link-btn.danger:hover { color:#b00020; }
        .ms-auto { margin-left:auto; }

        .chip-row { display:flex; flex-wrap:wrap; gap:.5rem; margin-bottom:.75rem; }
        .chip-toggle { border:none; background:rgba(0,0,0,.06); color:rgba(0,0,0,.75); border-radius:50px; padding:.65rem 1rem; font-size:.84rem; font-weight:700; cursor:pointer; transition:all .15s ease; display:inline-flex; align-items:center; line-height:1; min-height:40px; font-family:inherit; }
        .chip-toggle:hover { background:rgba(0,0,0,.12); color:#000; transform:translateY(-1px); }
        .chip-toggle-on { background:#000; color:#fff; }
        .chip-toggle-on:hover { background:#000; color:#fff; transform:none; }

        .search-box { display:flex; align-items:center; gap:8px; border:2px solid rgba(0,0,0,.15); border-radius:10px; padding:.1rem .8rem; margin-bottom:.75rem; background:#fff; }
        .search-input { border:none; outline:none; width:100%; padding:.65rem 0; font-size:.9rem; font-family:inherit; background:transparent; }

        .services-list { display:flex; flex-direction:column; gap:.6rem; }
        .services-list-title { font-weight:800; font-size:.82rem; }
        .service-row { background:rgba(0,0,0,.025); border:2px solid rgba(0,0,0,.1); border-radius:12px; padding:.8rem; display:flex; align-items:center; justify-content:space-between; gap:.75rem; flex-wrap:wrap; }
        .service-main { display:flex; align-items:center; gap:.55rem; flex:1; min-width:160px; }
        .service-name { font-weight:800; font-size:.9rem; }
        .primary-badge { background:#fff000; color:#000; font-size:.6rem; font-weight:900; padding:3px 7px; border-radius:999px; white-space:nowrap; }
        .service-controls { display:flex; align-items:center; gap:.45rem; }
        .rate-wrapper { display:flex; align-items:center; gap:.35rem; width:140px; }
        .currency-tag { font-size:.72rem; font-weight:800; color:rgba(0,0,0,.55); }
        .rate-input { padding:.6rem .7rem; font-size:.85rem; }
        .unit-select { width:130px; padding:.6rem .7rem; font-size:.85rem; }
        .svc-remove-btn { background:rgba(0,0,0,.06); border:none; color:#000; border-radius:8px; padding:9px 11px; cursor:pointer; display:inline-flex; align-items:center; justify-content:center; min-height:38px; }
        .svc-remove-btn:hover { background:#000; color:#fff; }

        .service-note { margin-top:.75rem; padding:.75rem .9rem; background:rgba(255,240,0,.14); border-left:3px solid #000; font-size:.78rem; line-height:1.5; }

        /* confirm strips */
        .confirm-inline { display:flex; align-items:center; justify-content:space-between; gap:.6rem; flex-wrap:wrap; background:#fffbd0; border:1.5px solid #000; border-radius:10px; padding:.55rem .75rem; margin-top:.6rem; font-size:.8rem; font-weight:700; flex:1; }
        .service-row .confirm-inline { margin-top:0; }
        .confirm-actions { display:inline-flex; gap:.4rem; }
        .confirm-yes { background:#000; color:#fff; border:none; border-radius:8px; padding:.45rem .8rem; font-size:.76rem; font-weight:800; cursor:pointer; font-family:inherit; }
        .confirm-no { background:#fff; color:#000; border:1.5px solid #000; border-radius:8px; padding:.45rem .8rem; font-size:.76rem; font-weight:800; cursor:pointer; font-family:inherit; }

        /* uploads */
        .file-zone { border:2px dashed rgba(0,0,0,.25); border-radius:14px; background:rgba(0,0,0,.02); cursor:pointer; transition:all .25s; overflow:hidden; min-height:150px; display:flex; align-items:center; justify-content:center; position:relative; }
        .file-zone:hover, .file-zone:focus-visible { border-color:#000; background:rgba(255,240,0,.06); outline:none; }
        .fz-filled { border-style:solid; border-color:#000; }
        .fz-error { border-color:#000 !important; background:rgba(255,240,0,.1); }
        .fz-placeholder { text-align:center; padding:1.5rem; color:rgba(0,0,0,.45); }
        .fz-text { display:block; font-weight:700; font-size:.85rem; }
        .fz-overlay { position:absolute; inset:0; background:rgba(0,0,0,.55); display:flex; flex-direction:column; align-items:center; justify-content:center; gap:5px; color:#fff; font-weight:700; font-size:.85rem; opacity:0; transition:opacity .2s; }
        .file-zone:hover .fz-overlay, .file-zone:focus-visible .fz-overlay { opacity:1; }
        .fname-row { display:flex; align-items:center; gap:.6rem; flex-wrap:wrap; margin-top:6px; }
        .fname-tag { margin-top:6px; font-size:.76rem; color:#000; display:flex; align-items:center; gap:5px; font-weight:700; background:rgba(255,240,0,.18); padding:5px 9px; border-radius:8px; width:fit-content; }
        .fname-row .fname-tag { margin-top:0; }

        .portfolio-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(120px, 1fr)); gap:.7rem; }
        .portfolio-thumb { position:relative; border-radius:12px; overflow:hidden; aspect-ratio:1; border:2px solid rgba(0,0,0,.15); }
        .portfolio-img { width:100%; height:100%; object-fit:cover; display:block; }
        .portfolio-cover-badge { position:absolute; top:6px; left:6px; background:#fff000; color:#000; font-size:.62rem; font-weight:900; padding:3px 7px; border-radius:50px; }
        .portfolio-new-badge { position:absolute; bottom:6px; left:6px; background:#000; color:#fff; font-size:.6rem; font-weight:900; padding:3px 7px; border-radius:50px; }
        .portfolio-remove { position:absolute; top:5px; right:5px; background:rgba(0,0,0,.7); color:#fff; border:none; border-radius:50%; width:30px; height:30px; cursor:pointer; display:flex; align-items:center; justify-content:center; }
        .portfolio-remove:hover { background:#fff000; color:#000; }
        .portfolio-confirm { position:absolute; inset:0; background:rgba(0,0,0,.85); color:#fff; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:.5rem; text-align:center; padding:.5rem; font-size:.72rem; font-weight:700; }
        .portfolio-confirm .confirm-yes { background:#fff000; color:#000; margin-right:.3rem; }
        .portfolio-add-slot { aspect-ratio:1; border:2px dashed rgba(0,0,0,.25); border-radius:12px; background:rgba(0,0,0,.02); cursor:pointer; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:4px; transition:all .2s; }
        .portfolio-add-slot:hover, .portfolio-add-slot:focus-visible { border-color:#000; background:rgba(255,240,0,.06); outline:none; }
        .photo-count { margin-left:auto; font-size:.72rem; color:rgba(0,0,0,.45); font-weight:700; }
        .add-photo-text { font-size:.75rem; font-weight:700; color:rgba(0,0,0,.5); }

        .account-info { background:rgba(255,240,0,.1); border:1.5px solid rgba(0,0,0,.12); border-radius:10px; padding:.8rem 1rem; font-size:.8rem; margin-top:.25rem; }
        .account-info ul { margin:.35rem 0 0; padding-left:1.1rem; }
        .account-divider { border:0; border-top:1px solid rgba(0,0,0,.08); margin:.5rem 0 1.25rem; }

        /* sticky action bar */
        .submit-area { position:sticky; bottom:0; z-index:20; background:#fff; border:2px solid #000; border-radius:14px; padding:.9rem 1.1rem; display:flex; align-items:center; justify-content:space-between; gap:1rem; flex-wrap:wrap; margin-top:1rem; box-shadow:0 -6px 24px rgba(0,0,0,.08); }
        .submit-title { font-size:.95rem; font-weight:900; margin-bottom:.15rem; }
        .submit-description { color:rgba(0,0,0,.6); font-size:.76rem; line-height:1.4; }
        .submit-actions { display:flex; gap:.6rem; align-items:center; }
        .save-btn { background:#fff000; border:none; box-shadow:0 2px 10px rgba(0,0,0,.18); border-radius:12px; color:#000; font-weight:900; font-size:.92rem; padding:.85rem 1.5rem; display:inline-flex; align-items:center; gap:8px; transition:all .2s; cursor:pointer; white-space:nowrap; font-family:inherit; }
        .save-btn:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 6px 18px rgba(255,240,0,.35); }
        .save-btn:disabled { opacity:.65; cursor:not-allowed; }
        .cancel-btn { background:#fff; border:2px solid rgba(0,0,0,.2); border-radius:12px; color:#000; font-weight:800; font-size:.88rem; padding:.8rem 1.2rem; cursor:pointer; font-family:inherit; }
        .cancel-btn:hover { border-color:#000; }
        .discard-note { font-weight:600; font-size:.74rem; flex-basis:100%; }

        .taxonomy-error { margin-bottom:1rem; padding:.8rem 1rem; border-radius:10px; background:rgba(255,240,0,.14); border:1px solid rgba(0,0,0,.15); font-size:.78rem; font-weight:700; }

        /* summary (view mode) */
        .sum-hero { display:flex; align-items:center; gap:1rem; background:#fff; border:2px solid rgba(0,0,0,.08); border-radius:16px; padding:1.25rem; margin-bottom:1rem; position:relative; overflow:hidden; flex-wrap:wrap; }
        .sum-hero::before { content:''; position:absolute; top:0; left:0; right:0; height:4px; background:#fff000; }
        .sum-avatar { width:72px; height:72px; border-radius:50%; object-fit:cover; border:3px solid #fff000; flex-shrink:0; }
        .sum-avatar-empty { background:#000; color:#fff000; display:flex; align-items:center; justify-content:center; font-weight:900; font-size:1.6rem; }
        .sum-hero-text { flex:1; min-width:160px; }
        .sum-name { font-size:1.15rem; font-weight:900; margin:0 0 2px; }
        .sum-sub { font-size:.82rem; color:rgba(0,0,0,.6); }
        .sum-type { display:inline-block; margin-top:6px; background:rgba(0,0,0,.07); border-radius:999px; padding:2px 10px; font-size:.7rem; font-weight:800; }
        .sum-block { background:#fff; border:2px solid rgba(0,0,0,.08); border-radius:14px; padding:1rem 1.15rem; margin-bottom:.85rem; }
        .sum-head { display:flex; align-items:center; justify-content:space-between; gap:.75rem; margin-bottom:.5rem; }
        .sum-title { display:flex; align-items:center; gap:8px; font-size:.9rem; font-weight:800; margin:0; }
        .sum-edit { display:inline-flex; align-items:center; gap:6px; background:#fff; border:1.5px solid #000; border-radius:999px; padding:.4rem .9rem; font-size:.76rem; font-weight:800; cursor:pointer; font-family:inherit; min-height:34px; }
        .sum-edit:hover { background:#fff000; }
        .sum-text { font-size:.88rem; line-height:1.6; margin:0 0 .35rem; }
        .sum-pre { white-space:pre-wrap; }
        .sum-empty { font-size:.82rem; color:rgba(0,0,0,.5); margin:0; }
        .sum-chip { background:#000; color:#fff; border-radius:999px; padding:.35rem .8rem; font-size:.76rem; font-weight:700; }
        .sum-list { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:.4rem; }
        .sum-list li { display:flex; justify-content:space-between; gap:1rem; flex-wrap:wrap; padding:.5rem 0; border-bottom:1px solid rgba(0,0,0,.07); font-size:.86rem; font-weight:700; }
        .sum-list li:last-child { border-bottom:none; }
        .sum-price { color:rgba(0,0,0,.65); font-weight:600; }
        .sum-thumbs { display:grid; grid-template-columns:repeat(auto-fill, minmax(84px, 1fr)); gap:.5rem; }
        .sum-thumb { width:100%; aspect-ratio:1; object-fit:cover; border-radius:10px; border:1.5px solid rgba(0,0,0,.12); }

        .danger-zone { border:2px solid rgba(176,0,32,.35); border-radius:14px; padding:1rem 1.15rem; margin-top:1.25rem; background:#fff7f8; }
        .danger-title { font-weight:900; font-size:.9rem; color:#b00020; margin-bottom:.25rem; }
        .danger-text { font-size:.8rem; margin:0 0 .6rem; }
        .danger-btn { background:#fff; color:#b00020; border:1.5px solid #b00020; border-radius:10px; padding:.55rem 1rem; font-weight:800; font-size:.8rem; cursor:pointer; display:inline-flex; align-items:center; gap:6px; font-family:inherit; }
        .danger-btn.solid { background:#b00020; color:#fff; }
        .danger-btn:disabled { opacity:.5; cursor:not-allowed; }
        .danger-actions { display:flex; gap:.5rem; margin-top:.6rem; flex-wrap:wrap; }
        .danger-confirm { display:flex; flex-direction:column; gap:.4rem; max-width:360px; }

        @media (max-width:767px) {
          .pf-section { padding:1.1rem; }
          .pf-section-content { padding-left:0; }
          .profile-step-number { position:static; width:32px; height:32px; min-width:32px; margin-bottom:.75rem; }
          .pf-input { font-size:16px; } /* stops phones zooming in when a field is tapped */
          .service-controls { width:100%; flex-wrap:wrap; }
          .rate-wrapper { flex:1; width:auto; }
          .unit-select { flex:1; }
          .svc-remove-btn { width:42px; }
          .submit-area { padding:.8rem; }
          .submit-actions { width:100%; }
          .submit-actions .save-btn { flex:1; justify-content:center; }
          .sum-edit-all { width:100%; justify-content:center; }
        }
        @media (max-width:480px) {
          .portfolio-grid { grid-template-columns:repeat(3, 1fr); }
        }
        @media (prefers-reduced-motion: reduce) {
          .chip-toggle, .save-btn, .file-zone { transition:none; }
        }
      `}</style>

      <div className="profile-flow" ref={topRef}>
        {craftsman?.status && <StatusBanner status={craftsman.status} />}

        <ValidationBanner message={validationError} onDismiss={() => setValidationError("")} />

        {taxonomyError && (
          <div className="taxonomy-error">
            <FaExclamationTriangle style={{ marginRight: 7 }} />
            {taxonomyError}
          </div>
        )}

        <ProgressCard
          percent={completion}
          steps={steps}
          onStepClick={(key) => (currentMode === "view" ? startEdit(key) : scrollToSection(key))}
        />

        {/* ═════════════ VIEW MODE ═════════════ */}
        {currentMode === "view" && (
          <ProfileSummary
            craftsman={craftsman}
            profileData={profileData}
            profileImage={profileImage}
            accountType={accountType}
            isCompany={isCompany}
            trade={trade}
            skills={skills}
            services={services}
            portfolioImages={portfolioImages}
            proofDocument={proofDocument}
            onEdit={startEdit}
            onDeleteProfile={onDeleteProfile}
            deleting={deleting}
          />
        )}

        {/* ═════════════ EDIT MODE ═════════════ */}
        {currentMode === "edit" && (
          <>
            {/* 1. Photo + account */}
            <div ref={sectionRefs.photo}>
              <Section number="1" completed={!!profileImage && (!isCompany || !!profileData.company_name?.trim())}>
                <Row>
                  <Col md={6}>
                    <FileInput
                      label="Your profile photo"
                      file={profileImage}
                      onChange={onProfileImage}
                      accept="image/*"
                      required
                      hint="Use a clear, recent photo of your face."
                    />
                  </Col>

                  <Col md={6}>
                    <ProofDocumentInput
                      proofDocument={proofDocument}
                      onChange={onProofDoc}
                      onRemove={onProofRemove}
                    />
                  </Col>
                </Row>

                <hr className="account-divider" />

                <div ref={sectionRefs.account}>
                  <AccountTypeSelector value={accountType} onChange={handleAccountTypeChange} />

                  <Collapse in={isCompany}>
                    <div>
                      <div style={{ paddingTop: "1rem" }}>
                        <TextInputWithDatalist
                          label="Company name"
                          name="company_name"
                          value={profileData.company_name}
                          onChange={handleInputChange}
                          icon={<FaBuilding />}
                          placeholder="Example: Mwangi Interiors Ltd"
                          required={isCompany}
                          helpText="Clients will see this business name on your profile and quotes."
                        />
                      </div>
                    </div>
                  </Collapse>
                </div>
              </Section>
            </div>

            {/* 2. About you */}
            <div ref={sectionRefs.description}>
              <Section number="2" completed={!!profileData.description?.trim()}>
                <TextInputWithDatalist
                  label="About you"
                  name="description"
                  value={profileData.description}
                  onChange={handleInputChange}
                  icon={<FaAlignLeft />}
                  placeholder="Example: I am a carpenter with 8 years of experience. I make custom furniture, kitchen cabinets and doors."
                  required
                  as="textarea"
                  rows={5}
                  maxLength={600}
                  showCount
                  helpText="Two or three short sentences are enough: your experience, the work you do and what clients can expect."
                />
              </Section>
            </div>

            {/* 3. Trade */}
            <div ref={sectionRefs.profession}>
              <Section number="3" completed={!!trade}>
                <ProfessionSelect
                  value={trade}
                  options={tradeOptions}
                  onChange={handleProfessionChange}
                  legacyValue={legacyProfession}
                  loading={taxonomyLoading}
                />

                {pendingTrade && (
                  <ConfirmInline
                    message={`Changing to ${pendingTrade.next || "no trade"} will remove ${
                      pendingTrade.lostSkills
                    } speciality(ies) and ${pendingTrade.lostServices} service(s). Continue?`}
                    confirmLabel="Yes, change"
                    cancelLabel="Keep my trade"
                    onConfirm={() => applyProfession(pendingTrade.next)}
                    onCancel={() => setPendingTrade(null)}
                  />
                )}
              </Section>
            </div>

            {/* 4. Specialities */}
            <div ref={sectionRefs.specialisation}>
              <Section number="4" completed={skills.length > 0}>
                <SkillsPicker
                  value={skills}
                  options={skillOptions}
                  onChange={handleSkillsChange}
                  disabled={!trade}
                  loading={taxonomyLoading}
                />
              </Section>
            </div>

            {/* 5. Services */}
            <div ref={sectionRefs.services}>
              <Section number="5" completed={services.length > 0}>
                <ServicesEditor
                  services={services}
                  serviceOptions={serviceOptions}
                  onToggle={toggleService}
                  onRemove={removeService}
                  onUpdateRate={updateServiceRate}
                  required
                  disabled={!trade}
                  touched={servicesTouched}
                  setTouched={setServicesTouched}
                  loading={taxonomyLoading}
                />
              </Section>
            </div>

            {/* 6. Area */}
            <div ref={sectionRefs.location}>
              <Section number="6" completed={!!profileData.location?.trim()}>
                <TextInputWithDatalist
                  label="Your area / town"
                  name="location"
                  value={profileData.location}
                  onChange={handleInputChange}
                  options={locations}
                  icon={<FaMapMarkerAlt />}
                  placeholder="Example: Westlands, Nairobi"
                  required
                  helpText="Start typing and pick from the list, or write your own. Clients use this to find craftsmen near them."
                />
              </Section>
            </div>

            {/* 7. Work photos */}
            <div ref={sectionRefs.portfolio}>
              <Section number="7" completed={portfolioImages.length > 0}>
                <PortfolioGallery
                  images={portfolioImages}
                  onAdd={onPhotoAdd}
                  onRemove={onPhotoRemove}
                  required
                />
              </Section>
            </div>

            {/* Action bar */}
            <div className="submit-area">
              <div>
                <div className="submit-title">
                  {missing.length === 0
                    ? "Everything is filled in"
                    : `${missing.length} thing${missing.length !== 1 ? "s" : ""} left to fill in`}
                </div>
                <div className="submit-description">
                  {dirty ? "You have unsaved changes." : missing.length === 0 ? "You can save now." : "Tap a step at the top to jump to it."}
                </div>
              </div>

              <div className="submit-actions">
                {hasSavedProfile && (
                  <button type="button" className="cancel-btn" onClick={requestCancel} disabled={saving}>
                    Cancel
                  </button>
                )}

                <button
                  type="button"
                  className="save-btn"
                  onClick={handleSubmit}
                  disabled={saving || taxonomyLoading}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" />
                      Saving...
                    </>
                  ) : taxonomyLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <FaSave />
                      {saveLabel}
                    </>
                  )}
                </button>
              </div>

              {confirmDiscard && (
                <div style={{ flexBasis: "100%" }}>
                  <ConfirmInline
                    message="Throw away your unsaved changes?"
                    confirmLabel="Yes, discard"
                    cancelLabel="Keep editing"
                    onConfirm={discardChanges}
                    onCancel={() => setConfirmDiscard(false)}
                  />
                  <div className="discard-note">
                    Text changes are undone. Photos you added or removed stay as they are until you
                    save or refresh the page.
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default ProfileTab;